import { z } from "zod";

/** Cumulative/tiered screen access model — legacy `POScreenLevelAccess` / `SLA_GetSupplierMatrixAccess`.
 * View Only ⊂ Confirm ⊂ Change Date ⊂ Change Quantity. */
export const ACCESS_TIERS = ["view_only", "confirm", "change_date", "change_quantity"] as const;
export type AccessTier = (typeof ACCESS_TIERS)[number];
export const ACCESS_TIER_RANK: Record<AccessTier, number> = {
  view_only: 0,
  confirm: 1,
  change_date: 2,
  change_quantity: 3,
};

export const ROLE_TYPES = ["SupplierRoles", "InternalRoles", "SystemRoles"] as const;
export type RoleType = (typeof ROLE_TYPES)[number];

/** Proposal lifecycle shared by PO and SLA/Firm-SLA confirmation. */
export const PROPOSAL_STATUSES = [
  "supplier_proposal",
  "approved_with_change",
  "supplier_proposal_rejected",
] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

/** Display labels for proposal status — shared so every screen (Proposal
 * Result, Proposal History Report, both modules) renders the real status
 * instead of a hardcoded string. */
export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  supplier_proposal: "Supplier Proposal",
  approved_with_change: "Approved with Change",
  supplier_proposal_rejected: "Supplier Proposal Rejected",
};

export const LINE_STATUSES = ["unconfirmed", "confirmed"] as const;
export type LineStatus = (typeof LINE_STATUSES)[number];

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type Pagination = z.infer<typeof paginationSchema>;

export function paginatedResponse<T>(rows: T[], limit: number) {
  const hasMore = rows.length > limit;
  const page = hasMore ? rows.slice(0, limit) : rows;
  return { rows: page, nextCursor: hasMore ? String(page.length) : null };
}

export const idParamSchema = z.object({ id: z.coerce.number().int().positive() });

export const dateFormats = ["DD/MMM/YYYY", "MM/DD/YYYY", "DD/MM/YYYY", "DD-MMM-YYYY", "MM-DD-YYYY", "DD-MM-YYYY"] as const;

export interface SessionUser {
  id: number;
  email: string;
  fullName: string;
  isSupplier: boolean;
  roles: string[];
  categories: string[];
  vendorIds: number[];
  plantIds: number[];
  initials: string;
}

export const auditActionSchema = z.object({
  action: z.string(),
  comment: z.string().optional(),
});
